/**
 * PIERRE HARDY · Cart drawer extras
 * ---------------------------------------------------------------------------
 * Adds three blocks to the Acidgreen <cart-drawer> (assets/cart-drawer.min.js)
 * without touching its minified bundle:
 *
 *   1. Reassurance lines under the items (delivery date range, free returns,
 *      customer service phone).
 *   2. Shopify express checkout buttons under "Valider ma commande".
 *   3. Product recommendations: a bag in the cart brings small leather goods
 *      in a matching colour, shoes bring a bag in the same colour tone; the
 *      remaining slots use the cart's category tags (SSFamily:, then ICC:).
 *
 * How it hooks in
 * - The drawer is a Lit element rendered in the light DOM. Its item list is
 *   the ".overflow-y-scroll" div and its summary is the ".bg-grey-background"
 *   div that holds a[href="/checkout"].
 * - Lit re-creates the summary when the cart goes from empty to full, so a
 *   MutationObserver re-mounts the blocks whenever they disappear.
 * - Cart data comes from the theme's own store (window.Stores "cart-store"),
 *   so the extras refresh on every add, remove or quantity change.
 *
 * Recommendations
 * - Product tags are not in /cart.js, so tags are read from /products/<handle>.js
 *   (cached for the session).
 * - Candidates are fetched with Shopify's tag URLs and the Section Rendering
 *   API: /collections/all/<tag-handle>+<gender-handle>?section_id=cart-drawer-reco
 * - Products already in the cart and sold-out products are excluded, and each
 *   model (StyleCode tag) appears once, never in a colour already in the cart.
 * - One-size products (bags, SLG: "TU") get a quick "Ajouter" button; shoes
 *   link to the product page so the shopper picks a size.
 * - Empty cart: three "Découvrir" rows (shoes, bags, men) set in the theme editor.
 */
(() => {
  'use strict';

  const SOURCE_ID = 'CartDrawerExtrasSource';
  const RECO_SECTION = 'cart-drawer-reco';
  const source = document.getElementById(SOURCE_ID);
  if (!source) return;

  let config;
  try {
    config = JSON.parse(source.querySelector('[data-cde-config]').textContent);
  } catch (error) {
    console.warn('[cart-drawer-extras] invalid config', error);
    return;
  }

  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  const productCache = new Map(); // handle -> product.js payload
  const recoCache = new Map(); // query key -> candidate list
  let lastCartSignature = '';
  let recoRequest = 0;

  /* --------------------------------------------------------------------- */
  /* Helpers                                                               */
  /* --------------------------------------------------------------------- */

  // Same algorithm as Shopify's `handleize`: "SSFamily:ANKLE BOOT" -> "ssfamily-ankle-boot"
  const handleize = (value) =>
    String(value)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[char]);

  const track = (event, detail) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...detail });
  };

  // Diagnostic mode: add ?cde_debug=1 to the page URL (kept for the session),
  // then open the browser console to see what each step found.
  let debug = /[?&]cde_debug=1/.test(window.location.search);
  try {
    if (debug) window.sessionStorage.setItem('cde_debug', '1');
    debug = debug || window.sessionStorage.getItem('cde_debug') === '1';
  } catch (error) { /* storage blocked: debug only for this page */ }
  const log = (...args) => { if (debug) console.info('[cart-drawer-extras]', ...args); };

  const parseCategoryMap = (text) => {
    const map = new Map();
    String(text || '')
      .split('\n')
      .map((line) => line.split('=>').map((part) => part.trim()))
      .filter((parts) => parts.length === 2 && parts[0] && parts[1])
      .forEach(([from, to]) => map.set(from.toLowerCase(), to));
    return map;
  };
  const categoryMap = parseCategoryMap(config.categoryMap);
  const prefixes = (config.categoryPrefixes || []).map((p) => p.trim()).filter(Boolean);

  /* Colour pairing --------------------------------------------------------
   * Bag in the cart     -> small leather goods (tag DesignationTheme:PM)
   * Shoes in the cart   -> bags (ICC:LEATHER GD without DesignationTheme:PM)
   * Same Color: tag first, then the same colour tone (groups set in the theme
   * editor, one tone per line), never a colour outside the tone.
   * ---------------------------------------------------------------------- */
  const pairing = config.pairing || {};
  const toneOf = new Map();
  String(pairing.tones || '')
    .split('\n')
    .forEach((line, index) => {
      line.split(',').map((c) => c.trim().toLowerCase()).filter(Boolean).forEach((c) => toneOf.set(c, index));
    });

  const tagValue = (tags, prefix) => {
    const tag = (tags || []).find((t) => t.toLowerCase().startsWith(prefix));
    return tag ? tag.slice(prefix.length).split(/[;,]/)[0].trim().toLowerCase() : '';
  };
  const hasTag = (tags, value) => (tags || []).some((t) => t.toLowerCase() === value);
  const kindOf = (tags) => {
    if (hasTag(tags, 'designationtheme:pm')) return 'slg';
    if (hasTag(tags, 'icc:leather gd')) return 'bag';
    if (hasTag(tags, 'icc:ladies sh') || hasTag(tags, 'icc:men shoes')) return 'shoes';
    return '';
  };
  const gendersOf = (tags) =>
    (tags || []).filter((t) => /^gender:/i.test(t)).map((t) => t.slice(7).trim().toLowerCase());
  // Mixed products suit everyone; otherwise one gender must match.
  const genderFits = (candidate, lead) =>
    !lead.length || lead.includes('mixt') || !candidate.length || candidate.includes('mixt') ||
    candidate.some((g) => lead.includes(g));

  // 2 = same colour, 1 = same tone, 0 = does not go with it.
  const colourScore = (candidateColour, leadColour) => {
    if (!candidateColour || !leadColour) return 0;
    if (candidateColour === leadColour) return 2;
    const a = toneOf.get(candidateColour);
    return a !== undefined && a === toneOf.get(leadColour) ? 1 : 0;
  };

  const PAIR_TARGETS = {
    bag: { enabled: pairing.bagToSlg, tagPath: 'designationtheme-pm', accepts: (c) => c.slg },
    shoes: { enabled: pairing.shoesToBag, tagPath: 'icc-leather-gd', accepts: (c) => !c.slg },
  };

  /* --------------------------------------------------------------------- */
  /* 1. Delivery date range (business days, Paris time)                    */
  /* --------------------------------------------------------------------- */

  // French public holidays with a fixed date; Easter-based ones are rare
  // enough in a 2–4 day window that the range stays honest without them.
  const FIXED_HOLIDAYS = ['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25'];

  const parisNow = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const get = (type) => Number(parts.find((p) => p.type === type).value);
    return { date: new Date(get('year'), get('month') - 1, get('day')), hour: get('hour') };
  };

  const isBusinessDay = (date) => {
    const day = date.getDay();
    const key = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return day !== 0 && day !== 6 && !FIXED_HOLIDAYS.includes(key);
  };

  const addBusinessDays = (start, count) => {
    const date = new Date(start);
    let added = 0;
    while (added < count) {
      date.setDate(date.getDate() + 1);
      if (isBusinessDay(date)) added += 1;
    }
    return date;
  };

  const deliveryText = () => {
    const { date, hour } = parisNow();
    // Orders after the cut-off (or on a non-working day) ship the next business day.
    const shipDay = isBusinessDay(date) && hour < config.delivery.cutoffHour ? date : addBusinessDays(date, 1);
    const locale = config.delivery.locale || document.documentElement.lang || 'fr-FR';
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    return config.delivery.template
      .replace('{min}', format.format(addBusinessDays(shipDay, config.delivery.min)))
      .replace('{max}', format.format(addBusinessDays(shipDay, config.delivery.max)));
  };

  /* --------------------------------------------------------------------- */
  /* Mounting into the Acidgreen drawer                                    */
  /* --------------------------------------------------------------------- */

  const getDrawer = () => document.querySelector('cart-drawer');

  const buildExtras = () => {
    const wrapper = document.createElement('div');
    wrapper.className = 'cde';
    wrapper.dataset.cdeMount = '';

    const reassurance = source.querySelector('[data-cde-reassurance]').content.cloneNode(true);
    const deliveryNode = reassurance.querySelector('[data-cde-delivery]');
    if (deliveryNode && config.delivery.template) deliveryNode.textContent = deliveryText();
    wrapper.appendChild(reassurance);

    const reco = document.createElement('section');
    reco.className = 'cde-reco';
    reco.hidden = true;
    reco.dataset.cdeReco = '';
    reco.setAttribute('aria-label', config.labels.recoTitle);
    reco.innerHTML = `<h3 class="cde-reco__title">${escapeHtml(config.labels.recoTitle)}</h3><div class="cde-reco__list" role="list"></div>`;
    wrapper.appendChild(reco);

    return wrapper;
  };

  /* Empty cart ------------------------------------------------------------
   * When the drawer opens on an empty cart, show three editorial rows
   * (shoes, bags, men's selection) rendered by the section in Liquid. They
   * hide as soon as an item is added and the usual blocks take over.
   * ---------------------------------------------------------------------- */
  const emptyConfig = config.empty || {};
  const buildEmpty = () => {
    const template = source.querySelector('[data-cde-empty]');
    return template ? template.content.firstElementChild.cloneNode(true) : null;
  };

  const mount = () => {
    const drawer = getDrawer();
    if (!drawer) return;

    // Under the line items: reassurance + recommendations.
    const itemList = drawer.querySelector('.overflow-y-scroll');
    const hasItems = Boolean(drawer.querySelector('cart-item'));
    let extras = drawer.querySelector('[data-cde-mount]');
    if (itemList && hasItems && !extras) {
      extras = buildExtras();
      itemList.appendChild(extras);
      lastCartSignature = ''; // force a recommendation refresh in the new node
      refresh();
    }
    if (extras) extras.hidden = !hasItems;

    // Empty cart: best sellers in the item list area.
    let empty = drawer.querySelector('[data-cde-empty-block]');
    if (itemList && !hasItems && emptyConfig.enabled && !empty) {
      empty = buildEmpty();
      if (empty) {
        itemList.appendChild(empty);
        track('cart_empty_links_view', {});
      }
    }
    if (empty) empty.hidden = hasItems;

    // Under "Valider ma commande": express checkout buttons.
    const express = document.querySelector('[data-cde-express]');
    const checkoutLink = drawer.querySelector('a[href$="/checkout"]');
    if (express && checkoutLink && express.previousElementSibling !== checkoutLink) {
      checkoutLink.insertAdjacentElement('afterend', express);
    }
  };

  /* --------------------------------------------------------------------- */
  /* 3. Recommendations by category tag                                    */
  /* --------------------------------------------------------------------- */

  const getProduct = async (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${root}products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  // Picks, from all items in the cart, the category tags to query, most
  // specific first, weighted by line price (the most expensive item leads).
  const buildQueries = (cart, products) => {
    const items = [...cart.items].sort((a, b) => b.final_line_price - a.final_line_price);
    const queries = [];
    const seen = new Set();

    prefixes.forEach((prefix) => {
      items.forEach((item) => {
        const product = products.get(item.handle);
        if (!product) return;
        const tags = product.tags || [];
        let category = tags.find((tag) => tag.toLowerCase().startsWith(prefix.toLowerCase()));
        if (!category) return;
        if (categoryMap.has(category.toLowerCase())) category = categoryMap.get(category.toLowerCase());

        const gender = config.matchGender ? tags.find((tag) => /^gender:/i.test(tag)) : null;
        const tagPath = [category, gender].filter(Boolean).map(handleize).join('+');
        if (!seen.has(tagPath)) {
          seen.add(tagPath);
          queries.push(tagPath);
        }
      });
    });
    return queries;
  };

  // `path` is a collection path relative to the shop root: "collections/all/<tags>"
  // for tag queries, or "collections/<handle>" for the empty-cart collection.
  // `keepOrder` keeps the collection's own order (the empty-cart collection is
  // curated by hand); tag queries use the sort chosen in the theme editor.
  const fetchList = async (path, keepOrder = false) => {
    if (!recoCache.has(path)) {
      const sort = !keepOrder && config.sortBy && config.sortBy !== 'manual' ? `&sort_by=${config.sortBy}` : '';
      const url = `${root}${path}?section_id=${RECO_SECTION}${sort}`;
      recoCache.set(
        path,
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const json = doc.querySelector('[data-cde-reco-products]');
            const list = json ? JSON.parse(json.textContent) : [];
            log('fetched', url, json ? `${list.length} products` : 'no product data in the response');
            return list;
          })
          .catch((error) => { log('fetch failed', url, error); return []; })
      );
    }
    return recoCache.get(path);
  };
  const fetchCandidates = (tagPath) => fetchList(`collections/all/${tagPath}`);

  const renderCard = (product, position) => {
    const quickAdd = product.single_variant && product.variant_id;
    const action = quickAdd
      ? `<button type="button" class="cde-card__action" data-cde-add="${product.variant_id}">${escapeHtml(config.labels.add)}</button>`
      : `<a class="cde-card__action" href="${product.url}">${escapeHtml(config.labels.chooseSize)}</a>`;
    const image = product.image
      ? `<img src="${product.image}" alt="${escapeHtml(product.image_alt || product.title)}" width="240" height="240" loading="lazy">`
      : '';
    return `
      <article class="cde-card" role="listitem" data-cde-position="${position}" data-cde-handle="${escapeHtml(product.handle)}">
        <a class="cde-card__media" href="${product.url}" tabindex="-1" aria-hidden="true">${image}</a>
        <a class="cde-card__title" href="${product.url}">${escapeHtml(product.title)}</a>
        <span class="cde-card__price">${escapeHtml(product.price)}</span>
        ${action}
      </article>`;
  };

  const renderRecommendations = async (cart) => {
    const drawer = getDrawer();
    const reco = drawer && drawer.querySelector('[data-cde-reco]');
    if (!reco) return;
    if (config.recoEnabled === false) { reco.hidden = true; return; }

    const requestId = ++recoRequest;
    const handles = [...new Set(cart.items.map((item) => item.handle))];
    const products = new Map(
      (await Promise.all(handles.map(async (handle) => [handle, await getProduct(handle)]))).filter(([, p]) => p)
    );
    const queries = buildQueries(cart, products);

    // One colour = one product in this catalogue: compare models (StyleCode tag,
    // title as fallback) so the cart never suggests the same bag in two colours,
    // nor another colour of a bag already in the cart.
    const styleOf = (tags, title) => {
      const tag = (tags || []).find((t) => /^stylecode:/i.test(t));
      return (tag ? tag.replace(/^stylecode:/i, '') : title || '').trim().toLowerCase();
    };
    const inCart = new Set(handles);
    const stylesInCart = new Set(
      cart.items.map((item) => {
        const product = products.get(item.handle);
        return styleOf(product && product.tags, item.product_title);
      })
    );
    const picks = [];
    const pickedStyles = new Set();
    let title = config.labels.recoTitle;

    // 1. Colour pairing. Every bag or pair of shoes in the cart gets a turn,
    //    most expensive first: when one finds no colour match (or not enough),
    //    the next one is tried before falling back to categories. The title
    //    names the first item that produced a match.
    const leads = [...cart.items]
      .sort((a, b) => b.final_line_price - a.final_line_price)
      .map((item) => ({ item, tags: (products.get(item.handle) || {}).tags || [] }))
      .filter(({ tags }) => {
        const target = PAIR_TARGETS[kindOf(tags)];
        return target && target.enabled;
      });
    for (const lead of leads) {
      if (picks.length >= config.limit) break;
      const target = PAIR_TARGETS[kindOf(lead.tags)];
      const leadColour = tagValue(lead.tags, 'color:');
      const leadGenders = gendersOf(lead.tags);
      const before = picks.length;
      // eslint-disable-next-line no-await-in-loop
      const candidates = await fetchCandidates(target.tagPath);
      candidates
        .map((candidate, rank) => ({ candidate, rank, score: colourScore(candidate.color, leadColour) }))
        .filter(({ candidate, score }) =>
          score > 0 && target.accepts(candidate) && genderFits(candidate.genders || [], leadGenders))
        .sort((a, b) => b.score - a.score || a.rank - b.rank) // same colour first, then best sellers
        .forEach(({ candidate }) => {
          if (picks.length >= config.limit) return;
          if (!candidate.available || inCart.has(candidate.handle)) return;
          const style = (candidate.style || candidate.title || '').trim().toLowerCase();
          if (stylesInCart.has(style) || pickedStyles.has(style)) return;
          pickedStyles.add(style);
          picks.push(candidate);
        });
      if (before === 0 && picks.length && config.labels.pairTitle) {
        title = config.labels.pairTitle.replace('{product}', lead.item.product_title);
      }
    }

    log('colour pairing', leads.map((l) => l.item.product_title), `${picks.length} picked`);

    // 2. Fill the remaining slots with the same category (SSFamily:, then ICC:).
    for (const tagPath of queries) {
      if (picks.length >= config.limit) break;
      // eslint-disable-next-line no-await-in-loop
      const candidates = await fetchCandidates(tagPath);
      candidates.forEach((candidate) => {
        if (picks.length >= config.limit) return;
        if (!candidate.available || inCart.has(candidate.handle)) return;
        const style = (candidate.style || candidate.title || '').trim().toLowerCase();
        if (stylesInCart.has(style) || pickedStyles.has(style)) return;
        pickedStyles.add(style);
        picks.push(candidate);
      });
    }

    log('category queries', queries, `${picks.length} picked in total`);

    if (requestId !== recoRequest) return; // a newer cart update is already rendering
    reco.querySelector('.cde-reco__title').textContent = title;
    reco.setAttribute('aria-label', title);
    const list = reco.querySelector('.cde-reco__list');
    list.innerHTML = picks.map(renderCard).join('');
    reco.hidden = picks.length === 0;
    if (picks.length) {
      track('cart_reco_impression', { cart_reco_handles: picks.map((p) => p.handle), cart_reco_source: queries[0] });
    }
  };

  /* --------------------------------------------------------------------- */
  /* Quick add for one-size products                                       */
  /* --------------------------------------------------------------------- */

  const cartStore = () => window.Stores && window.Stores.get('cart-store');

  const pushCartToTheme = async () => {
    const cart = await fetch(`${root}cart.js`).then((response) => response.json());
    const store = cartStore();
    if (store && store.value) store.value.set(cart); // re-renders the Acidgreen drawer
    return cart;
  };

  document.addEventListener('click', async (event) => {
    const emptyLink = event.target.closest('[data-cde-empty-link]');
    if (emptyLink) {
      track('cart_empty_link_click', { cart_empty_collection: emptyLink.dataset.cdeEmptyLink });
      return;
    }
    const card = event.target.closest('.cde-card');
    if (!card) return;
    const handle = card.dataset.cdeHandle;
    const position = Number(card.dataset.cdePosition) + 1;

    const button = event.target.closest('[data-cde-add]');
    if (!button) {
      if (event.target.closest('a')) track('cart_reco_click', { cart_reco_handle: handle, cart_reco_position: position });
      return;
    }

    button.disabled = true;
    button.textContent = config.labels.adding;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.cdeAdd), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`cart/add ${response.status}`);
      track('cart_reco_add', { cart_reco_handle: handle, cart_reco_position: position });
      await pushCartToTheme();
    } catch (error) {
      console.warn('[cart-drawer-extras] quick add failed', error);
      button.disabled = false;
      button.textContent = config.labels.add;
    }
  });

  /* --------------------------------------------------------------------- */
  /* Wiring                                                                */
  /* --------------------------------------------------------------------- */

  function refresh(cartFromStore) {
    const store = cartStore();
    const cart = cartFromStore || (store && store.value && store.value.get());
    if (!cart || !cart.items || !cart.items.length) return;
    const signature = cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
    if (signature === lastCartSignature) return;
    lastCartSignature = signature;
    renderRecommendations(cart);
  }

  const start = () => {
    const drawer = getDrawer();
    if (!drawer) return false;

    new MutationObserver(() => mount()).observe(drawer, { childList: true, subtree: true });

    const store = cartStore();
    if (store && typeof store.subscribe === 'function') {
      // The store de-duplicates subscribers by generatedId.
      store.subscribe((cart) => {
        mount();
        refresh(cart);
      }, { generatedId: 'cart-drawer-extras' });
    }

    if (window.PubSub) {
      window.PubSub.subscribe('cart-drawer:open', () => {
        mount();
        const delivery = drawer.querySelector('[data-cde-delivery]');
        if (delivery && config.delivery.template) delivery.textContent = deliveryText();
      });
    }

    mount();
    refresh();
    return true;
  };

  // The drawer is hydrated by <acidgreen-island client="load">, which may
  // finish after this script: wait for the custom element to be defined.
  if (!start()) {
    customElements.whenDefined('cart-drawer').then(() => requestAnimationFrame(start));
  }
})();
